import { useState, useCallback, useRef, useEffect } from "react";
import ColasService from "../api/colas.api";
import OrderService from "../../../services/order.service";
import StickerService from "../../../services/sticker.service";
import Utils from "../../../utils/Utils";
import { esEntradaSinPedido } from "../helpers/entradas";
import { construirSticker } from "../helpers/rutaMostrador";

const AVISO_SIN_RUTA = { tipo: "warning", mensaje: "No hay ruta de mostrador configurada." };
const AVISO_SIN_PEDIDO = { tipo: "info", mensaje: "Entrada sin pedido. No se añade a ruta." };

// Alta secuencial (no en paralelo) de cada albarán: no desordena los contadores de la ruta
const registrarAlbaranes = async ({ entrada, rutaMostrador, transportista, empleado, year }) => {
    const albaranes = await ColasService.getAlbaranes(entrada.id);
    for (const albaran of albaranes) {
        const { serie, order } = Utils.getOrder(albaran);
        const pedido = await OrderService.getCustomerOrder(serie, order, year);
        const sticker = construirSticker({ serie, order, pedido, ruta: rutaMostrador, transportista, empleado });
        await StickerService.registerSticker(sticker);
    }
};

// Devuelve el aviso que corresponde, o null si el alta en ruta fue bien
const anadirARuta = async (entrada, opciones) => {
    if (!opciones.rutaMostrador) return AVISO_SIN_RUTA;
    if (esEntradaSinPedido(entrada.numero)) return AVISO_SIN_PEDIDO;
    try {
        await registrarAlbaranes({ entrada, ...opciones });
        return null;
    } catch (err) {
        console.error("Error al añadir a ruta:", err);
        return {
            tipo: "warning",
            mensaje: `Entrada finalizada, pero falló al añadir a la ruta: ${err.message}`,
        };
    }
};

/**
 * Hook que ejecuta "finalizar entrada" y, opcionalmente, su alta en la ruta de
 * mostrador. Conserva el orden y la no atomicidad del legacy: finalizar va
 * SIEMPRE primero y, si el alta en ruta falla, la entrada queda finalizada y se
 * devuelve un aviso. No genera toasts: devuelve `avisos` para que la página los
 * muestre. `finalizar(entrada, opciones)` con opciones { anadirARuta, rutaMostrador,
 * transportista, empleado, year, onFinalizada }; `onFinalizada(entradaActualizada)`
 * se invoca justo tras finalizar, antes de tocar la ruta. Ignora (undefined) la
 * invocación si ya hay una en curso.
 * @returns {{
 *   finalizando: boolean,
 *   finalizar: (entrada: Object, opciones: Object) => Promise<{ entradaActualizada: Object, avisos: Array<{tipo: string, mensaje: string}> }|undefined>
 * }} Estado y acción de finalizar
 */
export const useFinalizarEntrada = () => {
    const [finalizando, setFinalizando] = useState(false);
    const enVuelo = useRef(false);
    const montado = useRef(true);

    useEffect(() => {
        montado.current = true;
        return () => {
            montado.current = false;
        };
    }, []);

    const finalizar = useCallback(async (entrada, { anadirARuta: anadir, onFinalizada, ...opciones }) => {
        if (enVuelo.current) return undefined;
        enVuelo.current = true;
        setFinalizando(true);
        try {
            const entradaActualizada = await ColasService.finalizar(entrada.id);
            if (onFinalizada) onFinalizada(entradaActualizada);
            const aviso = anadir ? await anadirARuta(entrada, opciones) : null;
            return { entradaActualizada, avisos: aviso ? [aviso] : [] };
        } finally {
            enVuelo.current = false;
            if (montado.current) setFinalizando(false);
        }
    }, []);

    return { finalizando, finalizar };
};
